import React, { useEffect, useState, useMemo } from "react";
import api, { API } from "../lib/api";
import { Check, X, Ban, RotateCcw, Trash2, UserPlus, Archive, Download, Lock, Unlock, KeyRound, Search, Users } from "lucide-react";
import { toast } from "sonner";
import GhlConnectionPanel from "../components/app/GhlConnectionPanel";
import AiSensyConnectionPanel from "../components/app/AiSensyConnectionPanel";
import AdminGapsAuditPanel from "../components/app/AdminGapsAuditPanel";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [tools, setTools] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [view, setView] = useState("active"); // "active" or "deleted"
  const [search, setSearch] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () =>
    Promise.all([
      api.get("/admin/users"),
      api.get("/admin/tools"),
      api.get("/admin/users/deleted").catch(() => ({ data: [] })),
    ]).then(([u, t, d]) => {
      setUsers(u.data);
      setTools(t.data);
      setDeleted(d.data);
    });

  useEffect(() => {
    load();
  }, []);

  const grant = async (toolId, currentlyHas) => {
    if (!selectedUser) return;
    try {
      await api.post("/admin/users/grant", {
        user_id: selectedUser.id,
        tool_id: toolId,
        grant: !currentlyHas,
      });
      const updated = await api.get("/admin/users");
      setUsers(updated.data);
      setSelectedUser(updated.data.find((u) => u.id === selectedUser.id));
    } catch {
      toast.error("Failed");
    }
  };

  const toggleDeny = async (toolId, currentlyDenied) => {
    if (!selectedUser) return;
    try {
      await api.post("/admin/users/deny", {
        user_id: selectedUser.id,
        tool_id: toolId,
        deny: !currentlyDenied,
      });
      const updated = await api.get("/admin/users");
      setUsers(updated.data);
      setSelectedUser(updated.data.find((u) => u.id === selectedUser.id));
      toast.success(!currentlyDenied ? "Locked for this user" : "Unlocked for this user");
    } catch {
      toast.error("Failed to update lock");
    }
  };

  const setRole = async (newRole) => {
    if (!selectedUser) return;
    if (
      !window.confirm(
        newRole === "admin"
          ? `Make ${selectedUser.name} an admin? They'll get full access to the admin console.`
          : `Demote ${selectedUser.name} to a regular member?`
      )
    )
      return;
    try {
      await api.post(`/admin/users/${selectedUser.id}/role`, { role: newRole });
      await load();
      const refreshed = (await api.get("/admin/users")).data.find(
        (u) => u.id === selectedUser.id
      );
      setSelectedUser(refreshed || null);
      toast.success(newRole === "admin" ? "Promoted to admin" : "Demoted to member");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change role");
    }
  };

  const setHubAccess = async (key, next) => {
    if (!selectedUser) return;
    try {
      await api.post(`/admin/users/${selectedUser.id}/hub-access`, { [key]: next });
      const updated = await api.get("/admin/users");
      setUsers(updated.data);
      setSelectedUser(updated.data.find((u) => u.id === selectedUser.id) || null);
      const label = key === "can_access_ai_office" ? "AI Office" : "Employz Intelligence";
      toast.success(next ? `${label} unlocked` : `${label} locked`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update access");
    }
  };

  const revokeUser = async () => {
    if (!selectedUser) return;
    if (
      !window.confirm(
        `Revoke ${selectedUser.name}'s access?\n\nThey can still log in and download PDFs of past conversations, but cannot create new chats. All conversations created after today will be inaccessible.`
      )
    )
      return;
    try {
      await api.post(`/admin/users/${selectedUser.id}/revoke`);
      await load();
      const refreshed = (await api.get("/admin/users")).data.find(
        (u) => u.id === selectedUser.id
      );
      setSelectedUser(refreshed || null);
      toast.success("Access revoked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke");
    }
  };

  const restoreAccess = async () => {
    if (!selectedUser) return;
    try {
      await api.post(`/admin/users/${selectedUser.id}/restore_access`);
      await load();
      const refreshed = (await api.get("/admin/users")).data.find(
        (u) => u.id === selectedUser.id
      );
      setSelectedUser(refreshed || null);
      toast.success("Access restored");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to restore");
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;
    if (
      !window.confirm(
        `Permanently delete ${selectedUser.name}?\n\nThey will be logged out and cannot sign up again with this email unless you restore it from Deleted Users.`
      )
    )
      return;
    try {
      await api.delete(`/admin/users/${selectedUser.id}`);
      toast.success("User deleted");
      setSelectedUser(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const resetPassword = async () => {
    if (!selectedUser) return;
    const generated = (() => {
      const chars =
        "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
      let out = "";
      for (let i = 0; i < 12; i++) {
        out += chars[Math.floor(Math.random() * chars.length)];
      }
      return out + "@1";
    })();
    const pw = window.prompt(
      `Set a new temporary password for ${selectedUser.name} (${selectedUser.email}).\n\nThey'll be forced to change it on next login. Existing JWTs will be invalidated.`,
      generated
    );
    if (!pw) return;
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/admin/users/${selectedUser.id}/reset-password`, {
        new_password: pw,
      });
      // Show password back to admin so they can copy/share via WhatsApp
      try { await navigator.clipboard.writeText(pw); } catch { /* clipboard may be blocked; not critical */ }
      toast.success("Password reset · copied to clipboard. Share with user via WhatsApp.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password");
    }
  };

  const restoreDeletedEmail = async (email) => {
    if (
      !window.confirm(
        `Remove "${email}" from the block-list?\n\nThey will be able to sign up again with the same email. Or use "Re-create user" to create the account directly.`
      )
    )
      return;
    try {
      await api.post(`/admin/users/deleted/${encodeURIComponent(email)}/restore`);
      toast.success("Email un-blocked");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("ai_hub_token");
      const res = await fetch(`${API}/admin/users/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `bbh-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Roster exported");
    } catch {
      toast.error("Could not export CSV");
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    const qDigits = q.replace(/\D/g, "");
    return users.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const phoneDigits = (u.whatsapp || "").replace(/\D/g, "");
      if (name.includes(q) || email.includes(q)) return true;
      if (qDigits && phoneDigits && phoneDigits.includes(qDigits)) return true;
      return false;
    });
  }, [users, search]);

  const filteredDeleted = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return deleted;
    const qDigits = q.replace(/\D/g, "");
    return deleted.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const phoneDigits = (u.whatsapp || "").replace(/\D/g, "");
      if (name.includes(q) || email.includes(q)) return true;
      if (qDigits && phoneDigits && phoneDigits.includes(qDigits)) return true;
      return false;
    });
  }, [deleted, search]);

  return (
    <div>
      {/* Tab switcher */}
      <div className="flex items-center gap-2 mb-4">
        <button
          data-testid="admin-tab-active"
          onClick={() => { setView("active"); setSelectedUser(null); }}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
            view === "active"
              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
              : "bg-transparent text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-white"
          }`}
        >
          Active · {users.length}
        </button>
        <button
          data-testid="admin-tab-deleted"
          onClick={() => { setView("deleted"); setSelectedUser(null); }}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
            view === "deleted"
              ? "bg-rose-500/10 text-rose-300 border-rose-500/30"
              : "bg-transparent text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-white"
          }`}
        >
          <Archive className="w-3.5 h-3.5" /> Deleted · {deleted.length}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="bulk-grant-btn"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 transition-colors"
            title="Grant one tool to many users at once"
          >
            <Users className="w-3.5 h-3.5" /> Bulk Grant
          </button>
          <button
            data-testid="export-users-csv-btn"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
            title="Download active users as CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Search — filter by name, email, or phone (WhatsApp) */}
      <div className="mb-4 relative">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          data-testid="admin-users-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or phone…"
          className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-zinc-950/60 border border-zinc-800 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-emerald-500/40 focus:ring-1 focus:ring-emerald-500/20 transition-colors"
        />
        {search && (
          <button
            data-testid="admin-users-search-clear"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200 transition-colors"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
      <div className="border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-900 text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center justify-between">
          <span>
            {view === "active"
              ? `${filteredUsers.length} of ${users.length} members`
              : `${filteredDeleted.length} of ${deleted.length} deleted`}
          </span>
          {search && (
            <span className="normal-case tracking-normal text-zinc-600">filtered</span>
          )}
        </div>
        {view === "active" ? (
          <div className="divide-y divide-zinc-900 max-h-[70vh] overflow-y-auto">
            {filteredUsers.length === 0 && (
              <div
                data-testid="admin-users-empty"
                className="text-center py-12 text-zinc-500 text-sm px-6"
              >
                {search ? `No members match "${search}".` : "No members yet."}
              </div>
            )}
            {filteredUsers.map((u) => (
              <button
                key={u.id}
                data-testid={`admin-user-${u.id}`}
                onClick={() => setSelectedUser(u)}
                className={`w-full text-left px-4 py-3 hover:bg-zinc-900/40 transition-colors flex items-center gap-3 ${
                  selectedUser?.id === u.id ? "bg-zinc-900/60" : ""
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-emerald-400 text-sm font-semibold">
                  {u.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{u.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {u.role === "admin" && (
                    <span className="text-[9px] uppercase tracking-[0.2em] text-amber-300 border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 rounded-full">
                      admin
                    </span>
                  )}
                  {u.status === "revoked" && (
                    <span className="text-[9px] uppercase tracking-[0.2em] text-rose-300 border border-rose-500/30 bg-rose-500/5 px-2 py-0.5 rounded-full">
                      revoked
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-zinc-900 max-h-[70vh] overflow-y-auto">
            {filteredDeleted.length === 0 && (
              <div className="text-center py-12 text-zinc-500 text-sm">
                {search ? `No deleted users match "${search}".` : "No deleted users."}
              </div>
            )}
            {filteredDeleted.map((u, i) => (
              <button
                key={(u.email || "") + i}
                data-testid={`admin-deleted-${u.email}`}
                onClick={() => setSelectedUser(u)}
                className={`w-full text-left px-4 py-3 hover:bg-zinc-900/40 transition-colors flex items-center gap-3 ${
                  selectedUser?.email === u.email && !selectedUser?.id ? "bg-zinc-900/60" : ""
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-300 text-sm font-semibold">
                  {u.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{u.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                </div>
                <div className="text-[10px] text-zinc-600 text-right whitespace-nowrap">
                  {u.deleted_at ? new Date(u.deleted_at).toLocaleDateString() : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border border-zinc-900 rounded-2xl bg-zinc-950/40 p-5">
        {!selectedUser ? (
          <div className="text-center py-20 text-zinc-500 text-sm">
            Select a member to manage access.
          </div>
        ) : view === "deleted" ? (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-rose-300">
              Deleted user
            </div>
            <h3 className="font-display text-2xl tracking-tighter font-black mt-1">
              {selectedUser.name}
            </h3>
            <div className="text-sm text-zinc-500">{selectedUser.email}</div>
            <div className="text-[11px] text-zinc-600 mt-1">
              Deleted on{" "}
              {selectedUser.deleted_at
                ? new Date(selectedUser.deleted_at).toLocaleString()
                : "—"}{" "}
              by {selectedUser.deleted_by || "admin"}
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ProfileField label="WhatsApp" value={selectedUser.whatsapp} mono />
              <ProfileField label="Business" value={selectedUser.business_name} />
              <ProfileField label="Industry" value={selectedUser.industry} />
              <ProfileField label="Annual Revenue" value={selectedUser.annual_revenue} />
              <ProfileField label="Org Strength" value={selectedUser.org_strength} />
              <ProfileField label="Projected Growth" value={selectedUser.projected_growth} />
            </div>

            <div className="mt-6 p-4 rounded-xl border border-rose-500/20 bg-rose-500/5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-rose-300 mb-2">
                Block-list active
              </div>
              <div className="text-sm text-zinc-300">
                Anyone trying to sign up with{" "}
                <span className="font-mono">{selectedUser.email}</span> is blocked
                and shown: "This email was previously used. Please contact admin."
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  data-testid="restore-deleted-btn"
                  onClick={() => restoreDeletedEmail(selectedUser.email)}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Un-block this email
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Member
                </div>
                <h3 className="font-display text-2xl tracking-tighter font-black mt-1">
                  {selectedUser.name}
                </h3>
                <div className="text-sm text-zinc-500">{selectedUser.email}</div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span
                  className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${
                    selectedUser.role === "admin"
                      ? "text-amber-300 border-amber-500/30 bg-amber-500/5"
                      : "text-zinc-400 border-zinc-700"
                  }`}
                >
                  {selectedUser.role}
                </span>
                {selectedUser.role === "admin" ? (
                  <button
                    data-testid="demote-admin-btn"
                    onClick={() => setRole("user")}
                    className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-zinc-800 text-zinc-400 hover:text-rose-300 hover:border-rose-500/40 transition-colors"
                  >
                    Demote to Member
                  </button>
                ) : (
                  <button
                    data-testid="promote-admin-btn"
                    onClick={() => setRole("admin")}
                    className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 transition-colors"
                  >
                    Make Admin
                  </button>
                )}
              </div>
            </div>

            {/* Business profile (collected at signup) */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ProfileField label="WhatsApp" value={selectedUser.whatsapp} mono />
              <ProfileField label="Business" value={selectedUser.business_name} />
              <ProfileField label="Industry" value={selectedUser.industry} />
              <ProfileField label="Annual Revenue" value={selectedUser.annual_revenue} />
              <ProfileField label="Org Strength" value={selectedUser.org_strength} />
              <ProfileField label="Projected Growth" value={selectedUser.projected_growth} />
            </div>

            {/* Hub-access toggles (admin bypasses; disabled for admins) */}
            <div className="mt-6 p-4 rounded-xl border border-zinc-800 bg-zinc-900/30">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Hub access
              </div>
              <div className="text-xs text-zinc-500 mt-1 mb-3">
                Members start locked out of these features. Toggle on to unlock them for this user.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <HubAccessToggle
                  testid="toggle-employz-access"
                  label="Employz Intelligence"
                  hint="Sync CRM, run Goldmine / Missed Money / Lead Hunter / Field Review"
                  checked={selectedUser.role === "admin" || !!selectedUser.can_access_employz}
                  disabled={selectedUser.role === "admin"}
                  onChange={(v) => setHubAccess("can_access_employz", v)}
                />
                <HubAccessToggle
                  testid="toggle-ai-office-access"
                  label="AI Office"
                  hint="Cross-hub handoff to the AI Office (Hub 2)"
                  checked={selectedUser.role === "admin" || !!selectedUser.can_access_ai_office}
                  disabled={selectedUser.role === "admin"}
                  onChange={(v) => setHubAccess("can_access_ai_office", v)}
                />
              </div>
            </div>

            <AdminGapsAuditPanel userId={selectedUser.id} />

            {/* Revoke / Delete actions */}
            <div className="mt-6 p-4 rounded-xl border border-zinc-800 bg-zinc-900/30 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  Access status
                </div>
                <div className="text-sm text-white mt-1">
                  {selectedUser.status === "revoked"
                    ? `Revoked on ${
                        selectedUser.revoked_at
                          ? new Date(selectedUser.revoked_at).toLocaleDateString()
                          : "—"
                      } — can download past PDFs only`
                    : "Active — full access"}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedUser.status === "revoked" ? (
                  <button
                    data-testid="restore-access-btn"
                    onClick={restoreAccess}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restore access
                  </button>
                ) : (
                  <button
                    data-testid="revoke-access-btn"
                    onClick={revokeUser}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 transition-colors"
                  >
                    <Ban className="w-3.5 h-3.5" /> Revoke access
                  </button>
                )}
                <button
                  data-testid="delete-user-btn"
                  onClick={deleteUser}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete user
                </button>
                <button
                  data-testid="reset-password-btn"
                  onClick={resetPassword}
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-700 text-zinc-300 hover:text-amber-300 hover:border-amber-500/40 transition-colors"
                  title="Set a new temporary password and invalidate existing tokens"
                >
                  <KeyRound className="w-3.5 h-3.5" /> Reset password
                </button>
              </div>
            </div>

            {/* GoHighLevel connection (Integration point A from LEAD_SCORING_BUILD.md) */}
            <GhlConnectionPanel userId={selectedUser.id} />
            {/* AiSensy WhatsApp channel */}
            <AiSensyConnectionPanel userId={selectedUser.id} />

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                Per-agent access
              </div>
              <div className="space-y-2">
                {tools.map((t) => {
                  const granted = (selectedUser.accessible_tools || []).includes(t.id);
                  const denied = (selectedUser.denied_tools || []).includes(t.id);
                  const isFree = t.access_type === "free";
                  const restricted = !!selectedUser.restricted_mode;
                  // Effective access (mirrors backend logic)
                  let effective;
                  if (selectedUser.status === "revoked") effective = "revoked";
                  else if (t.status === "locked") effective = "global-lock";
                  else if (denied) effective = "user-locked";
                  else if (restricted) effective = granted ? "open" : "needs-grant";
                  else if (isFree) effective = "open";
                  else effective = granted ? "open" : "needs-grant";

                  return (
                    <div
                      key={t.id}
                      data-testid={`access-row-${t.id}`}
                      className="flex items-center justify-between border border-zinc-900 rounded-lg px-3 py-2.5 gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{t.name}</div>
                        <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                          <span>{t.access_type} · {t.status}</span>
                          {effective === "open" && (
                            <span className="text-emerald-400">· can use</span>
                          )}
                          {effective === "user-locked" && (
                            <span className="text-rose-400">· locked for this user</span>
                          )}
                          {effective === "needs-grant" && (
                            <span className="text-amber-300">· needs grant</span>
                          )}
                          {effective === "global-lock" && (
                            <span className="text-zinc-500">· globally locked</span>
                          )}
                          {effective === "revoked" && (
                            <span className="text-rose-400">· user revoked</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Grant toggle (only meaningful for paid/private OR restricted users) */}
                        {(!isFree || restricted) && (
                          <button
                            data-testid={`grant-btn-${t.id}`}
                            onClick={() => grant(t.id, granted)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] uppercase tracking-[0.18em] border transition-colors ${
                              granted
                                ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
                                : "border-zinc-800 text-zinc-400 hover:border-emerald-500/30 hover:text-emerald-300"
                            }`}
                            title={granted ? "Click to revoke grant" : "Click to grant access"}
                          >
                            {granted ? (<><Check className="w-3 h-3" /> granted</>) : (<><X className="w-3 h-3" /> grant</>)}
                          </button>
                        )}
                        {/* Per-user lock toggle (works for ALL tools, free or paid) */}
                        <button
                          data-testid={`lock-btn-${t.id}`}
                          onClick={() => toggleDeny(t.id, denied)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] uppercase tracking-[0.18em] border transition-colors ${
                            denied
                              ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
                              : "border-zinc-800 text-zinc-400 hover:border-rose-500/40 hover:text-rose-300"
                          }`}
                          title={denied ? "Click to unlock for this user" : "Lock this agent for this user"}
                        >
                          {denied ? (<><Lock className="w-3 h-3" /> locked for this user</>) : (<><Unlock className="w-3 h-3" /> lock</>)}
                        </button>
                      </div>
                    </div>
                  );
                })}
                {tools.length === 0 && (
                  <div className="text-zinc-500 text-sm">No agents yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    <BulkGrantModal
      open={bulkOpen}
      onClose={() => setBulkOpen(false)}
      tools={tools}
      users={users}
      onDone={load}
    />
    </div>
  );
}

const ProfileField = ({ label, value, mono }) => (
  <div className="border border-zinc-900 bg-zinc-950/40 rounded-lg px-3 py-2.5">
    <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500">
      {label}
    </div>
    <div
      className={`text-sm text-white mt-1 ${mono ? "font-mono" : ""} ${
        !value ? "text-zinc-600 italic" : ""
      }`}
    >
      {value || "Not provided"}
    </div>
  </div>
);

const HubAccessToggle = ({ label, hint, checked, disabled, onChange, testid }) => (
  <div
    data-testid={`${testid}-row`}
    className={`border rounded-lg px-3 py-3 flex items-center justify-between gap-3 ${
      checked
        ? "border-emerald-500/25 bg-emerald-500/5"
        : "border-zinc-800 bg-zinc-950/40"
    }`}
  >
    <div className="min-w-0">
      <div className="text-sm text-white">{label}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{hint}</div>
      {disabled && (
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mt-1">
          Admin — always on
        </div>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-emerald-500" : "bg-zinc-700"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  </div>
);


const BulkGrantModal = ({ open, onClose, tools, users, onDone }) => {
  const [toolId, setToolId] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [modalSearch, setModalSearch] = useState("");
  const [mode, setMode] = useState("grant"); // "grant" or "revoke"
  const [busy, setBusy] = useState(false);

  // Reset state whenever the modal opens/closes so it feels fresh each time.
  useEffect(() => {
    if (open) {
      setToolId("");
      setSelectedIds(new Set());
      setModalSearch("");
      setMode("grant");
    }
  }, [open]);

  const sortedTools = useMemo(() => {
    // Mentorship agents first (these are the ones admins gate access to), then Action Program.
    const rank = (t) => (t.program === "mentorship" ? 0 : 1);
    return [...tools].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [tools]);

  const filteredMembers = useMemo(() => {
    const q = modalSearch.trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    return users.filter((u) => {
      if (u.status === "revoked") return false; // hide revoked members
      if (!q) return true;
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const phone = (u.whatsapp || "").replace(/\D/g, "");
      if (name.includes(q) || email.includes(q)) return true;
      if (digits && phone && phone.includes(digits)) return true;
      return false;
    });
  }, [users, modalSearch]);

  const toggle = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((s) => {
      const next = new Set(s);
      const allSelected = filteredMembers.every((u) => next.has(u.id));
      if (allSelected) filteredMembers.forEach((u) => next.delete(u.id));
      else filteredMembers.forEach((u) => next.add(u.id));
      return next;
    });
  };

  const submit = async () => {
    if (!toolId) {
      toast.error("Pick a tool first");
      return;
    }
    if (selectedIds.size === 0) {
      toast.error("Pick at least one member");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/admin/users/bulk-grant", {
        tool_id: toolId,
        user_ids: Array.from(selectedIds),
        grant: mode === "grant",
      });
      const { tool_name, modified, matched } = res.data;
      toast.success(
        mode === "grant"
          ? `Granted ${tool_name} to ${modified} of ${matched} members`
          : `Revoked ${tool_name} from ${modified} of ${matched} members`
      );
      await onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const allVisibleSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((u) => selectedIds.has(u.id));

  return (
    <div
      data-testid="bulk-grant-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-zinc-900 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Bulk access
            </div>
            <h3 className="font-display text-xl tracking-tight font-black mt-0.5">
              Grant one agent to many members
            </h3>
          </div>
          <button
            data-testid="bulk-grant-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tool + mode row */}
        <div className="px-6 pt-5 pb-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              Agent
            </span>
            <select
              data-testid="bulk-grant-tool-select"
              value={toolId}
              onChange={(e) => setToolId(e.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-white outline-none focus:border-emerald-500/40 transition-colors"
            >
              <option value="">— Choose an agent —</option>
              {sortedTools.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.program === "mentorship" ? "★ " : ""}
                  {t.name} · {t.access_type}
                </option>
              ))}
            </select>
          </label>
          <div className="inline-flex items-center gap-1 p-1 rounded-lg border border-zinc-800 bg-zinc-900/50">
            <button
              data-testid="bulk-mode-grant"
              onClick={() => setMode("grant")}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-[0.18em] transition-colors ${
                mode === "grant"
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              Grant
            </button>
            <button
              data-testid="bulk-mode-revoke"
              onClick={() => setMode("revoke")}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-[0.18em] transition-colors ${
                mode === "revoke"
                  ? "bg-rose-500/15 text-rose-300"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              Revoke
            </button>
          </div>
        </div>

        {/* Members search + select-all */}
        <div className="px-6 pb-2 flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="bulk-grant-user-search"
              type="text"
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              placeholder="Filter members by name, email or phone…"
              className="w-full pl-10 pr-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-emerald-500/40 transition-colors"
            />
          </div>
          <button
            data-testid="bulk-grant-toggle-all"
            onClick={toggleAllVisible}
            className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-800 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-300 transition-colors whitespace-nowrap"
          >
            {allVisibleSelected ? "Clear visible" : "Select visible"}
          </button>
        </div>

        {/* Members list */}
        <div className="flex-1 min-h-0 px-6 pb-3 overflow-y-auto">
          <div className="border border-zinc-900 rounded-lg divide-y divide-zinc-900 bg-zinc-950/40">
            {filteredMembers.length === 0 && (
              <div className="text-center py-10 text-zinc-500 text-sm">
                No members match "{modalSearch}".
              </div>
            )}
            {filteredMembers.map((u) => {
              const already = (u.accessible_tools || []).includes(toolId);
              const checked = selectedIds.has(u.id);
              return (
                <label
                  key={u.id}
                  data-testid={`bulk-user-row-${u.id}`}
                  className={`flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-900/40 transition-colors cursor-pointer ${
                    checked ? "bg-zinc-900/60" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(u.id)}
                    className="w-4 h-4 accent-emerald-500 shrink-0"
                    data-testid={`bulk-user-check-${u.id}`}
                  />
                  <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-emerald-400 text-xs font-semibold shrink-0">
                    {u.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{u.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {u.email}
                      {u.whatsapp ? ` · ${u.whatsapp}` : ""}
                    </div>
                  </div>
                  {toolId && already && (
                    <span className="text-[9px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-500/30 bg-emerald-500/5 px-2 py-0.5 rounded-full shrink-0">
                      already granted
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-900 flex items-center justify-between gap-3">
          <div className="text-xs text-zinc-500">
            {selectedIds.size} member{selectedIds.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="bulk-grant-submit"
              disabled={busy || !toolId || selectedIds.size === 0}
              onClick={submit}
              className={`text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md border transition-colors ${
                mode === "grant"
                  ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  : "border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {busy
                ? "Working…"
                : mode === "grant"
                ? `Grant to ${selectedIds.size} member${selectedIds.size === 1 ? "" : "s"}`
                : `Revoke from ${selectedIds.size} member${selectedIds.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
